import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { BiCheck, BiPlus } from 'react-icons/bi';
import { MdDragHandle, MdOutlineHomeRepairService } from 'react-icons/md';
import { TbSpeedboat } from 'react-icons/tb';
import { useNavigate } from 'react-router';

import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { useLeaveEntityPage } from '@/hooks/useLeaveEntityPage';
import { useNoYachtFallback } from '@/hooks/useNoYachtFallback';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { getRolesOrdering, updateRolesOrdering } from '@/store/persons/person.api';
import type { PersonRole } from '@/store/persons/person.dto';
import { usePerson } from '@/store/persons/person.store';
import { useCurrentServiceProvider } from '@/store/service-providers/current-service-provider.store';
import { refreshAfterAccountSwitch } from '@/store/service-providers/refresh-active-account';
import { useAccountServiceProviderQuery } from '@/store/service-providers/service-providers.store';
import { useVessels } from '@/store/vessels/vessels.store';

import { Avatar } from '../Avatar';

import s from './AccountSwitchContent.module.scss';

type AccountSwitchContentProps = {
  /** Off for someone who is only ever a guest: there is nothing for them to manage or add. */
  canManage: boolean;
  onClose: () => void;
  onAddAccount: () => void;
};

const ROLES_ORDERING_KEY = ['roles-ordering'];
const ROLES_ORDERING_STALE_TIME = 10 * 60_000;
/** Longer than the stale time: the switcher unmounts on close, and would drop it at 5. */
const ROLES_ORDERING_GC_TIME = 30 * 60_000;
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } };
const TOUCH_SENSOR_OPTIONS = { activationConstraint: { delay: 120, tolerance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

const SKELETON_ROWS = 3;

const SkeletonAvatar = () => <div className={`${s.accountImage} ${s.skeleton}`} />;
const SkeletonName = () => <span className={s.skeletonText} style={{ width: '55%' }} />;
const SkeletonMeta = () => <span className={s.skeletonText} style={{ width: '35%' }} />;

// Mirrors the markup of `Account` so rows don't shift once the ordering loads.
const AccountSkeleton = () => (
  <article className={`${s.account} ${s.accountSkeleton}`} aria-hidden="true">
    <div className={s.accountBody}>
      <SkeletonAvatar />
      <div className={s.accountInfo}>
        <h6>
          <SkeletonName />
        </h6>
        <p>
          <SkeletonMeta />
        </p>
      </div>
    </div>
    <span className={s.dragHandle}>
      <span className={`${s.dragHandleSkeleton} ${s.skeleton}`} />
    </span>
  </article>
);

export const AccountSwitchContent = ({
  canManage,
  onClose,
  onAddAccount,
}: AccountSwitchContentProps) => {
  const NoYachtFallback = useNoYachtFallback();
  const navigate = useNavigate();
  const leaveEntityPage = useLeaveEntityPage();
  const queryClient = useQueryClient();

  const { currentUser } = usePerson();
  const { vessel, selectVessel, myVessels } = useVessels();
  const { selectServiceProvider, selectedServiceProviderId } = useCurrentServiceProvider();

  // Only this switcher changes the order, and its drag writes the cache before the server,
  // so the copy in hand stays right between opens. An account switch invalidates every
  // query, which covers the rest.
  const { data: rolesOrdering, isSuccess: isOrderingSuccess } = useQuery({
    queryKey: ROLES_ORDERING_KEY,
    queryFn: getRolesOrdering,
    staleTime: ROLES_ORDERING_STALE_TIME,
    gcTime: ROLES_ORDERING_GC_TIME,
  });

  const pendingCount = currentUser?.services?.length || SKELETON_ROWS;

  const orderedServices = useMemo(() => {
    const services = currentUser?.services ?? [];
    if (!rolesOrdering || rolesOrdering.length === 0) return services;
    const orderMap = new Map(rolesOrdering.map((r) => [r.service_id, r.order]));
    return [...services].sort(
      (a, b) =>
        (orderMap.get(a.service_id) ?? Number.MAX_SAFE_INTEGER) -
        (orderMap.get(b.service_id) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [currentUser?.services, rolesOrdering]);

  const { mutate: persistOrdering } = useMutation({
    mutationFn: updateRolesOrdering,
    onMutate: async (items) => {
      await queryClient.cancelQueries({ queryKey: ROLES_ORDERING_KEY });
      const previous =
        queryClient.getQueryData<{ service_id: string; order: number }[]>(ROLES_ORDERING_KEY);
      queryClient.setQueryData(ROLES_ORDERING_KEY, items);
      return { previous };
    },
    onError: (_err, _items, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ROLES_ORDERING_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ROLES_ORDERING_KEY });
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedServices.findIndex((s) => s.service_id === active.id);
    const newIndex = orderedServices.findIndex((s) => s.service_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(orderedServices, oldIndex, newIndex);
    persistOrdering(moved.map((service, i) => ({ service_id: service.service_id, order: i + 1 })));
  };

  const handleAccountChange = async (serviceId: string, serviceType: string) => {
    if (serviceId === useActiveAccount.getState().serviceId) {
      return;
    }
    if (serviceType === 'vessel') {
      selectVessel(serviceId);
    } else {
      selectServiceProvider(serviceId);
    }
    onClose();
    await refreshAfterAccountSwitch();
    leaveEntityPage();
  };

  const renderVessel = (service: PersonRole) => {
    const v = myVessels?.find(({ id }) => id === service.service_id);

    return (
      <Account
        key={service.service_id}
        id={service.service_id}
        role={service.role}
        name={v?.vessel_name ?? ''}
        loading={!myVessels}
        selected={service.service_id === vessel?.id}
        onSelect={() => handleAccountChange(service.service_id, service.service_type)}
        avatar={
          <AuthorizedImage
            className={s.accountImage}
            src={imageVariantLink(v?.image, 32)}
            fallbackSrc={NoYachtFallback}
            alt={'image'}
          />
        }
      />
    );
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedServices.map((service) => service.service_id)}
          strategy={verticalListSortingStrategy}
        >
          <section className={s.list}>
            {!isOrderingSuccess &&
              Array.from({ length: pendingCount }).map((_, index) => (
                <AccountSkeleton key={index} />
              ))}
            {isOrderingSuccess &&
              orderedServices.map((service) =>
                service.service_type === 'vessel' ? (
                  renderVessel(service)
                ) : (
                  <ServiceProviderAccount
                    key={service.service_id}
                    service={service}
                    selected={selectedServiceProviderId === service.service_id}
                    onSelect={() => handleAccountChange(service.service_id, service.service_type)}
                  />
                ),
              )}
          </section>
        </SortableContext>
      </DndContext>
      {canManage && (
        <section className={s.menuFooter}>
          <button
            type="button"
            className={s.manage}
            onClick={() => {
              onClose();
              navigate(myVessels?.length === 1 ? `/vessels/${myVessels[0].id}` : '/vessels');
            }}
          >
            <TbSpeedboat size={18} />
            Manage Vessel
          </button>
          <button
            type="button"
            className={s.manage}
            onClick={() => {
              onClose();
              navigate('/my-services');
            }}
          >
            <MdOutlineHomeRepairService size={18} />
            Manage Business
          </button>
          <button type="button" className={s.manage} onClick={onAddAccount}>
            <BiPlus size={18} />
            Add Account
          </button>
        </section>
      )}
    </>
  );
};

const Account = ({
  id,
  selected,
  onSelect,
  role,
  name,
  meta,
  avatar,
  loading,
}: {
  id: string;
  selected?: boolean;
  onSelect(): void;
  role: string;
  name: string;
  meta?: string;
  avatar: React.ReactNode;
  loading?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`${s.account} ${selected ? s.accountActive : ''}`}
    >
      <div
        className={s.accountBody}
        role="option"
        aria-selected={!!selected}
        onClick={loading ? undefined : onSelect}
      >
        {loading ? <SkeletonAvatar /> : avatar}
        <div className={s.accountInfo}>
          <h6 title={loading ? undefined : name}>{loading ? <SkeletonName /> : name}</h6>
          <p>{loading ? <SkeletonMeta /> : meta || role}</p>
        </div>
        {!loading && selected && <BiCheck size={20} className={s.selected} />}
      </div>
      <button
        type="button"
        className={s.dragHandle}
        aria-label={`Reorder ${name}`}
        {...attributes}
        {...listeners}
      >
        <MdDragHandle size={20} />
      </button>
    </article>
  );
};

const ServiceProviderAccount = ({
  service,
  selected,
  onSelect,
}: {
  service: PersonRole;
  selected?: boolean;
  onSelect(): void;
}) => {
  const { data, isLoading } = useAccountServiceProviderQuery(service.service_id);

  return (
    <Account
      key={service.service_id}
      id={service.service_id}
      role="Service Provider"
      name={data?.name ?? ''}
      meta="Service Provider"
      selected={selected}
      onSelect={onSelect}
      loading={isLoading}
      avatar={<Avatar firstName={data?.name} src={imageVariantLink(data?.logo, 32)} size={32} />}
    />
  );
};
